export interface PickupSecurityPolicy {
  requireGuardianVerification: boolean;
  allowSupervisorOverride: boolean;
  lockoutThreshold: number;
  lockoutWindowMinutes: number;
  countDeniedAuthAsFailedAttempt: boolean;
}

export const PICKUP_SECURITY_BOUNDS = {
  lockoutThreshold: { min: 1, max: 10, default: 3 },
  lockoutWindowMinutes: { min: 1, max: 60, default: 15 },
} as const;

export const DEFAULT_PICKUP_SECURITY: PickupSecurityPolicy = {
  requireGuardianVerification: true,
  allowSupervisorOverride: true,
  lockoutThreshold: PICKUP_SECURITY_BOUNDS.lockoutThreshold.default,
  lockoutWindowMinutes: PICKUP_SECURITY_BOUNDS.lockoutWindowMinutes.default,
  countDeniedAuthAsFailedAttempt: false,
};

function coerceBool(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

export function normalizePickupSecurity(raw: unknown): PickupSecurityPolicy {
  const r = (raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  const b = PICKUP_SECURITY_BOUNDS;
  const legacyGuardianOptOut = r.requireGuardianVerification === false;
  return {
    // KC-E3: a pickup code identifies the family but does not authorize a
    // release. All checkout surfaces require a listed caregiver or a logged,
    // staff-only override; the former opt-out setting is intentionally ignored.
    requireGuardianVerification: true,
    // Old organizations could turn the former guardian gate off and also leave
    // its override toggle off. The effective mandatory gate must retain a
    // staff-only release path for those records; it never restores code-only
    // checkout because the server still requires an override reason.
    allowSupervisorOverride:
      legacyGuardianOptOut ||
      coerceBool(r.allowSupervisorOverride, DEFAULT_PICKUP_SECURITY.allowSupervisorOverride),
    lockoutThreshold: clampInt(r.lockoutThreshold, b.lockoutThreshold.min, b.lockoutThreshold.max, b.lockoutThreshold.default),
    lockoutWindowMinutes: clampInt(r.lockoutWindowMinutes, b.lockoutWindowMinutes.min, b.lockoutWindowMinutes.max, b.lockoutWindowMinutes.default),
    countDeniedAuthAsFailedAttempt: coerceBool(r.countDeniedAuthAsFailedAttempt, DEFAULT_PICKUP_SECURITY.countDeniedAuthAsFailedAttempt),
  };
}
